'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React from 'react'
import Icon from '@mdi/react'
import {
  mdiAndroid,
  mdiApple,
  mdiAppleSafari,
  mdiArch,
  mdiCentos,
  mdiDebian,
  mdiDevices,
  mdiFedora,
  mdiFirefox,
  mdiFreebsd,
  mdiGoogleChrome,
  mdiLaptop,
  mdiLinux,
  mdiLinuxMint,
  mdiManjaro,
  mdiMicrosoftEdge,
  mdiMicrosoftWindows,
  mdiMonitor,
  mdiOpera,
  mdiRedhat,
  mdiUbuntu
} from '@mdi/js'

// walrus
import Box from '@components/kit/box'

// lib
import { AuthDevice } from '@store/Auth/types'

/* -----------------------------------------------------------------------------
 * constants
 *
 * Flipper keeps only a free text name and a platform per device, so the icons
 * are read from the name: "Chrome on macOS" from this app, a phone model from
 * the member app. The colors are each brand's own.
 * -------------------------------------------------------------------------- */

type BrandIcon = { path: string; color: string }

// specific systems before the generic ones they contain: ChromeOS before the
// Chrome browser, every distribution before Linux
const systems: [RegExp, BrandIcon][] = [
  [/chrome ?os/i, { path: mdiLaptop, color: '#4285F4' }],
  [/mac ?os|iphone|ipad|\bios\b|apple/i, { path: mdiApple, color: '#1D1D1F' }],
  [/windows/i, { path: mdiMicrosoftWindows, color: '#0078D4' }],
  [/android|galaxy|pixel/i, { path: mdiAndroid, color: '#3DDC84' }],
  [/ubuntu/i, { path: mdiUbuntu, color: '#E95420' }],
  [/mint/i, { path: mdiLinuxMint, color: '#87CF3E' }],
  [/debian/i, { path: mdiDebian, color: '#A80030' }],
  [/fedora/i, { path: mdiFedora, color: '#51A2DA' }],
  [/manjaro/i, { path: mdiManjaro, color: '#35BF5C' }],
  [/\barch\b/i, { path: mdiArch, color: '#1793D1' }],
  [/centos/i, { path: mdiCentos, color: '#932279' }],
  [/red ?hat/i, { path: mdiRedhat, color: '#EE0000' }],
  [/freebsd/i, { path: mdiFreebsd, color: '#AB2B28' }],
  [/linux/i, { path: mdiLinux, color: '#1D1D1F' }]
]

// Edge and Opera names never include Chrome, but keep them first anyway
const browsers: [RegExp, BrandIcon][] = [
  [/edge/i, { path: mdiMicrosoftEdge, color: '#0078D7' }],
  [/opera/i, { path: mdiOpera, color: '#FF1B2D' }],
  [/firefox/i, { path: mdiFirefox, color: '#FF7139' }],
  [/safari/i, { path: mdiAppleSafari, color: '#006CFF' }],
  [/chrome(?! ?os)/i, { path: mdiGoogleChrome, color: '#4285F4' }]
]

// when the name says nothing, the platform still does
const platforms: Record<AuthDevice['device']['platform'], BrandIcon> = {
  web: { path: mdiMonitor, color: '#3E4088' },
  ios: { path: mdiApple, color: '#1D1D1F' },
  android: { path: mdiAndroid, color: '#3DDC84' },
  other: { path: mdiDevices, color: '#3E4088' }
}

const matchIcon = (list: [RegExp, BrandIcon][], name: string) =>
  list.find(([pattern]) => pattern.test(name))?.[1]

/* -----------------------------------------------------------------------------
 * DeviceIcon
 *
 * The system fills the tile; the browser, when the name has one, sits on its
 * corner.
 * -------------------------------------------------------------------------- */

export type DeviceIconProps = {
  device: AuthDevice['device']
  muted?: boolean
}

function DeviceIcon({ device, muted = false }: DeviceIconProps) {
  const system =
    matchIcon(systems, device.name) ??
    platforms[device.platform] ??
    platforms.other
  const browser = matchIcon(browsers, device.name)

  return (
    <Box
      alignItems='center'
      bg='proSurfaceAlt'
      borderColor='proBorder'
      borderRadius='proLg'
      borderStyle='solid'
      borderWidth={1}
      flex='0 0 auto'
      height={48}
      justifyContent='center'
      opacity={muted ? 0.45 : 1}
      position='relative'
      width={48}
    >
      <Icon path={system.path} size='26px' color={system.color} />
      {browser ? (
        <Box
          alignItems='center'
          bg='proSurface'
          borderColor='proBorder'
          borderRadius='proPill'
          borderStyle='solid'
          borderWidth={1}
          bottom={-6}
          height={24}
          justifyContent='center'
          position='absolute'
          right={-6}
          width={24}
        >
          <Icon path={browser.path} size='16px' color={browser.color} />
        </Box>
      ) : null}
    </Box>
  )
}

export default DeviceIcon
